import type { GroupeBareme } from './familles.js';

/**
 * Chartes d'embauche (docs/CHARTES.md). Le contenu vit ici, en français et en
 * arabe, bloc par bloc : la version bilingue met chaque paragraphe en regard de
 * sa traduction. Toute modification du texte incrémente `VERSION_CHARTE`, pour
 * qu'une charte signée renvoie toujours au texte exact qu'elle portait.
 *
 * ATTENTION — les mentions légales sont celles de la spécification et n'ont pas
 * été relues par un juriste : le document porte cette réserve en pied de page.
 */
export const VERSION_CHARTE = 1;

export const LANGUES_CHARTE = ['fr', 'ar', 'bilingue'] as const;
export type LangueCharte = (typeof LANGUES_CHARTE)[number];

export const LIBELLES_LANGUE_CHARTE: Record<LangueCharte, string> = {
  fr: 'Français',
  ar: 'Arabe',
  bilingue: 'Bilingue (deux colonnes)',
};

/** docs/CHARTES.md : arabe pour les postes de terrain, français pour les autres. */
export const LANGUE_CHARTE_PAR_DEFAUT: Record<GroupeBareme, LangueCharte> = {
  chauffeur: 'ar',
  production: 'ar',
  magasin: 'ar',
  vente: 'ar',
  commercial: 'fr',
  administratif: 'fr',
};

/** Le bilingue est recommandé pour les postes de terrain. */
export const BILINGUE_RECOMMANDE: readonly GroupeBareme[] = ['chauffeur', 'production', 'magasin', 'vente'];

export interface Texte {
  fr: string;
  ar: string;
}

export interface SectionCharte {
  cle: string;
  titre: Texte;
  paragraphes: Texte[];
}

export interface ChoixDeclaration {
  cle: string;
  texte: Texte;
}

/**
 * Une ligne de cases à cocher, une seule réponse. Chaque déclaration alimente
 * un axe du barème (docs/BAREME.md § Grille de notation) : la charte signée est
 * la source, le formulaire d'évaluation ne fait que la reprendre.
 */
export interface Declaration {
  cle: string;
  axe: 'tabac' | 'alcool';
  question: Texte;
  choix: ChoixDeclaration[];
}

const OUI_NON = (prefixe: string): ChoixDeclaration[] => [
  { cle: `${prefixe}.oui`, texte: { fr: 'Oui', ar: 'نعم' } },
  { cle: `${prefixe}.non`, texte: { fr: 'Non', ar: 'لا' } },
];

export const TITRES_CHARTE = {
  document: { fr: 'Charte d’embauche', ar: 'ميثاق الانتداب' },
  entreprise: { fr: 'Tiba Invest — Plaisirs Gourmands', ar: 'Tiba Invest — Plaisirs Gourmands' },
  identification: { fr: 'Identification', ar: 'البيانات' },
  candidat: { fr: 'Candidat', ar: 'المترشّح' },
  poste: { fr: 'Poste', ar: 'الخطة' },
  reference: { fr: 'Référence de l’annonce', ar: 'مرجع الإعلان' },
  lieux: { fr: 'Point(s) de vente', ar: 'نقاط البيع' },
  edition: { fr: 'Édition du', ar: 'تاريخ الإصدار' },
  declarations: { fr: 'Déclarations du candidat', ar: 'تصريحات المترشّح' },
  consigneDeclarations: {
    fr: 'Cocher une seule case par ligne, sans rature.',
    ar: 'يُؤشَّر على خانة واحدة في كل سطر، دون شطب.',
  },
  signatures: { fr: 'Signatures', ar: 'الإمضاءات' },
  luApprouve: {
    fr: 'Mention manuscrite « Lu et approuvé »',
    ar: 'عبارة « قرأت ووافقت » بخط اليد',
  },
  faitA: { fr: 'Fait à ………………………… le ……………………', ar: 'حُرّر بـ ………………………… في ……………………' },
  signatureCandidat: { fr: 'Signature du candidat', ar: 'إمضاء المترشّح' },
  signatureEntreprise: { fr: 'Pour l’entreprise (nom et signature)', ar: 'عن المؤسسة (الاسم والإمضاء)' },
  paraphe: { fr: 'Paraphe candidat …… / entreprise ……', ar: 'تأشيرة المترشّح …… / المؤسسة ……' },
  reserveJuridique: {
    fr: 'Version de travail — mentions légales à faire valider par un juriste',
    ar: 'نسخة عمل — البنود القانونية في انتظار مصادقة مختصّ قانوني',
  },
  exemplaires: {
    fr: 'Établie en deux exemplaires, l’un remis au candidat.',
    ar: 'حُرّر في نظيرين، سُلّم أحدهما إلى المترشّح.',
  },
} satisfies Record<string, Texte>;

const TRONC_COMMUN: SectionCharte[] = [
  {
    cle: 'poste',
    titre: { fr: 'Nature du poste et horaires', ar: 'طبيعة الخطة وأوقات العمل' },
    paragraphes: [
      {
        fr:
          'Le poste, ses missions et ses horaires sont ceux décrits dans l’annonce référencée ci-dessus. ' +
          'Le candidat déclare en avoir pris connaissance et être disponible pour ces horaires, y compris ' +
          'le travail posté, de nuit ou le week-end lorsqu’ils y sont prévus.',
        ar:
          'إنّ الخطة ومهامها وأوقات العمل هي تلك المبيّنة في الإعلان المشار إلى مرجعه أعلاه. ويصرّح ' +
          'المترشّح بأنه اطّلع عليها وأنه متاح للعمل وفقها، بما في ذلك العمل بالتناوب أو الليلي أو في ' +
          'نهاية الأسبوع متى كان ذلك مقرّرًا.',
      },
    ],
  },
  {
    cle: 'sincerite',
    titre: { fr: 'Sincérité des déclarations', ar: 'صدق التصريحات' },
    paragraphes: [
      {
        fr:
          'Le candidat déclare, sous sa seule responsabilité, que les informations figurant dans son CV, ' +
          'fournies lors des entretiens et cochées dans la présente charte sont exactes et complètes. Ces ' +
          'déclarations sont déterminantes dans la décision de recrutement.',
        ar:
          'يصرّح المترشّح، على مسؤوليته الخاصة، بأنّ المعلومات الواردة في سيرته الذاتية والمقدّمة خلال ' +
          'المقابلات والمؤشَّر عليها في هذا الميثاق صحيحة وكاملة. وتُعدّ هذه التصريحات حاسمة في قرار ' +
          'الانتداب.',
      },
    ],
  },
  {
    cle: 'justificatifs',
    titre: { fr: 'Justificatifs et rapport de référence', ar: 'الوثائق المثبتة وتقرير المرجع' },
    paragraphes: [
      {
        fr:
          'Le candidat s’engage à remettre, dans un délai de sept jours calendaires à compter de la ' +
          'signature, les justificatifs demandés — pièce d’identité, diplômes et attestations de travail, ' +
          'extrait du bulletin n° 3, ainsi que tout document propre au poste — et à signer le rapport de ' +
          'référence dans le même délai. À défaut, la candidature est classée sans suite et toute promesse ' +
          'd’embauche devient caduque.',
        ar:
          'يلتزم المترشّح بتقديم الوثائق المطلوبة في أجل سبعة أيام كاملة بداية من تاريخ الإمضاء — بطاقة ' +
          'التعريف، الشهائد وشهادات العمل، مضمون السوابق العدلية (البطاقة عدد 3)، وكل وثيقة خاصة ' +
          'بالخطة — وبإمضاء تقرير المرجع في نفس الأجل. وفي صورة عدم الالتزام، يُحفظ الترشّح دون متابعة ' +
          'ويسقط كل وعد بالانتداب.',
      },
    ],
  },
  {
    cle: 'fausse_declaration',
    titre: { fr: 'Fausse déclaration', ar: 'التصريح الكاذب' },
    paragraphes: [
      {
        fr:
          'Toute information fausse, incomplète ou dissimulée, découverte à tout moment — y compris après ' +
          'la signature du contrat de travail ou après la période d’essai — peut entraîner la nullité du ' +
          'contrat et la rupture pour faute.',
        ar:
          'كل معلومة كاذبة أو منقوصة أو مخفاة، يتمّ اكتشافها في أيّ وقت — بما في ذلك بعد إمضاء عقد الشغل ' +
          'أو بعد انتهاء فترة التجربة — يمكن أن تؤدّي إلى بطلان العقد وإنهائه بسبب خطأ.',
      },
    ],
  },
];

const CONFIDENTIALITE: SectionCharte = {
  cle: 'confidentialite',
  titre: { fr: 'Confidentialité et loyauté', ar: 'السرّية والنزاهة' },
  paragraphes: [
    {
      fr:
        'Le salarié respecte la confidentialité des informations de l’entreprise, de ses clients et de ses ' +
        'fournisseurs, pendant et après le contrat.',
      ar: 'يحافظ الأجير على سرّية معلومات المؤسسة وحرفائها ومزوّديها، أثناء العقد وبعده.',
    },
    {
      fr:
        'Il déclare tout conflit d’intérêts et n’utilise les moyens de l’entreprise que pour les besoins ' +
        'du service.',
      ar: 'يُصرّح بكل تضارب في المصالح، ولا يستعمل وسائل المؤسسة إلا لحاجيات العمل.',
    },
  ],
};

/** Blocs spécifiques par groupe de familles (docs/CHARTES.md § Blocs spécifiques). */
const BLOCS: Record<GroupeBareme, SectionCharte> = {
  chauffeur: {
    cle: 'conduite',
    titre: { fr: 'Conduite et livraison', ar: 'السياقة والتوزيع' },
    paragraphes: [
      {
        fr:
          'Tolérance zéro alcool et stupéfiants pendant le service. Un contrôle d’alcoolémie peut être ' +
          'effectué avant chaque prise de poste ; aucune consommation d’alcool n’est admise dans les douze ' +
          'heures qui la précèdent.',
        ar:
          'عدم التسامح مطلقًا مع الكحول والمخدّرات أثناء العمل. ويمكن إجراء مراقبة لنسبة الكحول قبل كل ' +
          'مباشرة للعمل، ولا يُسمح بتناول الكحول خلال الاثنتي عشرة ساعة السابقة لها.',
      },
      {
        fr:
          'Le chauffeur détient un permis valide correspondant au véhicule et signale sans délai toute ' +
          'suspension ou tout retrait. Les amendes pour infraction au code de la route commise pendant le ' +
          'service restent à sa charge, dans les limites prévues par la loi.',
        ar:
          'يحمل السائق رخصة سياقة سارية المفعول تتلاءم مع العربة، ويُعلم فورًا بكل تعليق أو سحب لها. ' +
          'وتبقى الخطايا الناتجة عن مخالفات قانون الطرقات المرتكبة أثناء العمل على كاهله، في الحدود ' +
          'التي يسمح بها القانون.',
      },
      {
        fr:
          'Il respecte la chaîne du froid, vérifie l’état du véhicule avant chaque tournée et signale toute ' +
          'anomalie.',
        ar: 'يحترم سلسلة التبريد، ويتثبّت من حالة العربة قبل كل جولة، ويُبلّغ عن كل خلل.',
      },
    ],
  },
  production: {
    cle: 'hygiene',
    titre: { fr: 'Hygiène et production', ar: 'حفظ الصحة والإنتاج' },
    paragraphes: [
      {
        fr:
          'Il est interdit de fumer dans les zones de production ; les pauses se prennent aux plages ' +
          'prévues, hors zone, et sont suivies d’un lavage des mains.',
        ar:
          'يُمنع التدخين في فضاءات الإنتاج، وتُؤخذ فترات الاستراحة في الأوقات المحدّدة خارج هذه الفضاءات ' +
          'مع غسل اليدين عند العودة.',
      },
      {
        fr:
          'La tenue de travail est obligatoire et le lavage des mains systématique à chaque entrée en zone. ' +
          'Les règles HACCP et de traçabilité des lots s’appliquent.',
        ar:
          'لباس العمل إجباري وغسل اليدين واجب عند كل دخول إلى فضاء الإنتاج. وتنطبق قواعد HACCP وتتبّع ' +
          'الدفعات.',
      },
      {
        fr: 'Le téléphone personnel est interdit en zone de production.',
        ar: 'يُمنع استعمال الهاتف الشخصي داخل فضاء الإنتاج.',
      },
    ],
  },
  magasin: {
    cle: 'magasin',
    titre: { fr: 'Magasin et logistique', ar: 'المخزن واللوجستيك' },
    paragraphes: [
      {
        fr:
          'La conduite d’engins de manutention est réservée aux personnes autorisées. Tolérance zéro alcool ' +
          'pendant le service.',
        ar: 'قيادة آلات المناولة مخصّصة للأشخاص المرخّص لهم. وعدم التسامح مطلقًا مع الكحول أثناء العمل.',
      },
      {
        fr:
          'Les règles « premier entré, premier sorti » et le respect des dates limites s’appliquent. Le ' +
          'magasinier répond des écarts d’inventaire qui lui sont imputables.',
        ar:
          'تنطبق قاعدة « ما يدخل أولًا يخرج أولًا » واحترام تواريخ انتهاء الصلوحية. ويتحمّل المسؤول عن ' +
          'المخزن فوارق الجرد المنسوبة إليه.',
      },
    ],
  },
  vente: {
    cle: 'vente',
    titre: { fr: 'Vente et caisse', ar: 'البيع والصندوق' },
    paragraphes: [
      {
        fr: 'Une tenue soignée et la présentation fixée par l’enseigne sont de rigueur.',
        ar: 'يُشترط هندام لائق والمظهر المحدّد من قبل العلامة التجارية.',
      },
      {
        fr:
          'Il est interdit de fumer dans l’espace de vente ; toute pause cigarette se prend hors zone et est ' +
          'suivie d’un lavage des mains.',
        ar: 'يُمنع التدخين في فضاء البيع، وتُؤخذ كل استراحة تدخين خارجه مع غسل اليدين عند العودة.',
      },
      {
        fr:
          'Le vendeur répond de sa caisse et des écarts constatés, et remet les fonds au coffre selon la ' +
          'procédure.',
        ar: 'يتحمّل البائع مسؤولية صندوقه والفوارق المسجّلة، ويودع الأموال بالخزنة وفق الإجراءات المعمول بها.',
      },
    ],
  },
  commercial: CONFIDENTIALITE,
  administratif: CONFIDENTIALITE,
};

const CONTROLE_ALCOOL: Record<GroupeBareme, Texte> = {
  chauffeur: {
    fr:
      'J’accepte le contrôle d’alcoolémie avant la prise de poste et l’absence de toute consommation dans ' +
      'les douze heures qui la précèdent',
    ar: 'أقبل مراقبة نسبة الكحول قبل مباشرة العمل وعدم تناول الكحول خلال الاثنتي عشرة ساعة السابقة لها',
  },
  magasin: {
    fr: 'J’accepte la tolérance zéro alcool pendant le service et le contrôle qui peut l’accompagner',
    ar: 'أقبل عدم التسامح مطلقًا مع الكحول أثناء العمل والمراقبة التي قد ترافقه',
  },
  production: {
    fr: 'J’accepte la tolérance zéro alcool pendant le service',
    ar: 'أقبل عدم التسامح مطلقًا مع الكحول أثناء العمل',
  },
  vente: {
    fr: 'J’accepte la tolérance zéro alcool pendant le service',
    ar: 'أقبل عدم التسامح مطلقًا مع الكحول أثناء العمل',
  },
  commercial: {
    fr: 'J’accepte la tolérance zéro alcool pendant le service',
    ar: 'أقبل عدم التسامح مطلقًا مع الكحول أثناء العمل',
  },
  administratif: {
    fr: 'J’accepte la tolérance zéro alcool pendant le service',
    ar: 'أقبل عدم التسامح مطلقًا مع الكحول أثناء العمل',
  },
};

/** Déclarations cochables (docs/CHARTES.md § Déclarations cochables). */
export function declarationsCharte(groupe: GroupeBareme): Declaration[] {
  return [
    {
      cle: 'tabac.habitude',
      axe: 'tabac',
      question: { fr: 'Tabac', ar: 'التدخين' },
      choix: [
        { cle: 'tabac.habitude.non_fumeur', texte: { fr: 'Non-fumeur', ar: 'غير مدخّن' } },
        {
          cle: 'tabac.habitude.ancien',
          texte: { fr: 'Ancien fumeur, arrêt depuis plus d’un an', ar: 'مدخّن سابق، منذ أكثر من سنة' },
        },
        { cle: 'tabac.habitude.occasionnel', texte: { fr: 'Fumeur occasionnel', ar: 'مدخّن من حين لآخر' } },
        { cle: 'tabac.habitude.regulier', texte: { fr: 'Fumeur régulier', ar: 'مدخّن بانتظام' } },
      ],
    },
    {
      cle: 'tabac.quatre_heures',
      axe: 'tabac',
      question: {
        fr: 'Je peux tenir quatre heures de poste sans pause cigarette',
        ar: 'أستطيع العمل أربع ساعات متواصلة دون استراحة تدخين',
      },
      choix: OUI_NON('tabac.quatre_heures'),
    },
    {
      cle: 'tabac.regles',
      axe: 'tabac',
      question: {
        fr: 'J’accepte les plages de pause fixes, les zones sans tabac et le lavage des mains',
        ar: 'أقبل أوقات الاستراحة المحدّدة والفضاءات الخالية من التدخين وغسل اليدين',
      },
      choix: OUI_NON('tabac.regles'),
    },
    {
      cle: 'alcool.consommation',
      axe: 'alcool',
      question: { fr: 'Alcool', ar: 'الكحول' },
      choix: [
        { cle: 'alcool.consommation.aucune', texte: { fr: 'Pas de consommation', ar: 'لا أتناول الكحول' } },
        {
          cle: 'alcool.consommation.occasionnelle',
          texte: { fr: 'Consommation occasionnelle, hors service', ar: 'من حين لآخر، خارج أوقات العمل' },
        },
        {
          cle: 'alcool.consommation.sans_reponse',
          texte: { fr: 'Je ne souhaite pas répondre', ar: 'لا أرغب في الإجابة' },
        },
      ],
    },
    {
      cle: 'alcool.controle',
      axe: 'alcool',
      question: CONTROLE_ALCOOL[groupe],
      choix: OUI_NON('alcool.controle'),
    },
  ];
}

function sectionDonnees(moisConservation: number): SectionCharte {
  return {
    cle: 'donnees',
    titre: { fr: 'Données personnelles', ar: 'المعطيات الشخصية' },
    paragraphes: [
      {
        fr:
          `Les informations recueillies servent uniquement au recrutement. Elles sont conservées ` +
          `${moisConservation} mois après le dernier contact, puis anonymisées. Le candidat peut y accéder ` +
          'et les faire rectifier en écrivant à rh@tiba-invest.com.',
        ar:
          'تُستعمل المعطيات المجمّعة لغرض الانتداب فقط، وتُحفظ لمدة ' +
          `${moisConservation} شهرًا بعد آخر اتصال ثم تُحذف منها هويّة أصحابها. ويمكن للمترشّح ` +
          'الاطّلاع عليها وطلب تصحيحها بمراسلة rh@tiba-invest.com.',
      },
    ],
  };
}

export interface ContenuCharte {
  version: number;
  sections: SectionCharte[];
  declarations: Declaration[];
  donnees: SectionCharte;
}

/** Tronc commun, bloc de la famille, déclarations, puis mention sur les données. */
export function composerCharte(groupe: GroupeBareme, options: { moisConservation: number }): ContenuCharte {
  return {
    version: VERSION_CHARTE,
    sections: [...TRONC_COMMUN, BLOCS[groupe]],
    declarations: declarationsCharte(groupe),
    donnees: sectionDonnees(options.moisConservation),
  };
}

/** Résultat de l'édition d'une charte vierge (POST /candidatures/:id/charte). */
export interface CharteEditee {
  charteSigneeId: string;
  documentId: string;
  nomFichier: string;
  langue: LangueCharte;
  version: number;
}
